import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Route as RouteIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowRightAlt as ArrowIcon,
} from '@mui/icons-material';

const Rutas = () => {
  const { isAdmin } = useAuth();
  const [rutas, setRutas] = useState([]);
  const [estaciones, setEstaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedRuta, setSelectedRuta] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    origen: '',
    destino: '',
    escala: 0,
    esc1: '',
    esc2: '',
    esc3: '',
    capacidad: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rutasRes, estacionesRes] = await Promise.all([
        api.get(endpoints.rutas.list),
        api.get(endpoints.guias.estaciones),
      ]);
      setRutas(rutasRes.data.data?.rutas || []);
      setEstaciones(estacionesRes.data.data || []);
    } catch (error) {
      setError('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, ruta = null) => {
    setDialogMode(mode);
    setSelectedRuta(ruta);
    if (ruta) {
      setFormData({
        code: ruta.code || '',
        origen: ruta.origen || '',
        destino: ruta.destino || '',
        escala: ruta.escala || 0,
        esc1: ruta.esc1 || '',
        esc2: ruta.esc2 || '',
        esc3: ruta.esc3 || '',
        capacidad: ruta.capacidad || 0,
      });
    } else {
      setFormData({
        code: '',
        origen: '',
        destino: '',
        escala: 0,
        esc1: '',
        esc2: '',
        esc3: '',
        capacidad: 0,
      });
    }
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRuta(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'escala' || name === 'capacidad' ? parseInt(value) || 0 : value,
    }));
  };

  const getRutaVisual = (data = formData) => {
    const partes = [data.origen];
    if (data.escala >= 1 && data.esc1) partes.push(data.esc1);
    if (data.escala >= 2 && data.esc2) partes.push(data.esc2);
    if (data.escala >= 3 && data.esc3) partes.push(data.esc3);
    partes.push(data.destino);
    return partes.filter(Boolean).join(' → ');
  };

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      setError('El código es requerido');
      return;
    }
    if (!formData.origen) {
      setError('El origen es requerido');
      return;
    }
    if (!formData.destino) {
      setError('El destino es requerido');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (dialogMode === 'edit' && selectedRuta) {
        await api.put(endpoints.rutas.update, { id: selectedRuta.id, ...formData });
        setSuccess('Ruta actualizada exitosamente');
      } else {
        await api.post(endpoints.rutas.create, formData);
        setSuccess('Ruta creada exitosamente');
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruta) => {
    if (!confirm(`¿Eliminar la ruta "${ruta.code}"?`)) return;

    try {
      await api.delete(endpoints.rutas.delete, { data: { id: ruta.id } });
      setSuccess('Ruta eliminada');
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error">No tiene permisos para acceder a esta sección</Alert>
        <Button component={Link} to="/vuelos" sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <RouteIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Administrar Rutas
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  Configuración de rutas de vuelo con escalas
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Nueva Ruta
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Código</TableCell>
                <TableCell>Ruta Completa</TableCell>
                <TableCell align="center">Escalas</TableCell>
                <TableCell align="right">Capacidad (kg)</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rutas.length > 0 ? (
                rutas.map((ruta) => (
                  <TableRow key={ruta.id} hover>
                    <TableCell>
                      <Chip label={ruta.code} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={ruta.origen} size="small" color="success" />
                        {ruta.escala >= 1 && ruta.esc1 && (
                          <>
                            <ArrowIcon fontSize="small" color="disabled" />
                            <Chip label={ruta.esc1} size="small" variant="outlined" />
                          </>
                        )}
                        {ruta.escala >= 2 && ruta.esc2 && (
                          <>
                            <ArrowIcon fontSize="small" color="disabled" />
                            <Chip label={ruta.esc2} size="small" variant="outlined" />
                          </>
                        )}
                        {ruta.escala >= 3 && ruta.esc3 && (
                          <>
                            <ArrowIcon fontSize="small" color="disabled" />
                            <Chip label={ruta.esc3} size="small" variant="outlined" />
                          </>
                        )}
                        <ArrowIcon fontSize="small" color="disabled" />
                        <Chip label={ruta.destino} size="small" color="error" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={ruta.escala}
                        size="small"
                        color={ruta.escala > 0 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{(ruta.capacidad || 0).toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog('edit', ruta)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(ruta)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <RouteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No hay rutas configuradas</Typography>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                      onClick={() => handleOpenDialog('create')}
                    >
                      Crear primera ruta
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions */}
      <Box sx={{ mt: 3 }}>
        <Button component={Link} to="/admin" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon color="primary" />
          {dialogMode === 'edit' ? `Editar Ruta: ${selectedRuta?.code}` : 'Nueva Ruta'}
          <IconButton onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Información Básica
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Código de Ruta"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="Ej: 471"
                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Origen</InputLabel>
                <Select name="origen" value={formData.origen} onChange={handleInputChange} label="Origen">
                  <MenuItem value="">Seleccione</MenuItem>
                  {estaciones.map((est) => (
                    <MenuItem key={est.id} value={est.codigo || est.code}>
                      {est.codigo || est.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Destino Final</InputLabel>
                <Select name="destino" value={formData.destino} onChange={handleInputChange} label="Destino Final">
                  <MenuItem value="">Seleccione</MenuItem>
                  {estaciones.map((est) => (
                    <MenuItem key={est.id} value={est.codigo || est.code}>
                      {est.codigo || est.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Escalas */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Escalas Intermedias
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Número de Escalas</InputLabel>
                <Select name="escala" value={formData.escala} onChange={handleInputChange} label="Número de Escalas">
                  <MenuItem value={0}>Sin escalas</MenuItem>
                  <MenuItem value={1}>1 escala</MenuItem>
                  <MenuItem value={2}>2 escalas</MenuItem>
                  <MenuItem value={3}>3 escalas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.escala >= 1 && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Escala 1</InputLabel>
                  <Select name="esc1" value={formData.esc1} onChange={handleInputChange} label="Escala 1">
                    <MenuItem value="">Seleccione</MenuItem>
                    {estaciones.map((est) => (
                      <MenuItem key={est.id} value={est.codigo || est.code}>
                        {est.codigo || est.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.escala >= 2 && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Escala 2</InputLabel>
                  <Select name="esc2" value={formData.esc2} onChange={handleInputChange} label="Escala 2">
                    <MenuItem value="">Seleccione</MenuItem>
                    {estaciones.map((est) => (
                      <MenuItem key={est.id} value={est.codigo || est.code}>
                        {est.codigo || est.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.escala >= 3 && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Escala 3</InputLabel>
                  <Select name="esc3" value={formData.esc3} onChange={handleInputChange} label="Escala 3">
                    <MenuItem value="">Seleccione</MenuItem>
                    {estaciones.map((est) => (
                      <MenuItem key={est.id} value={est.codigo || est.code}>
                        {est.codigo || est.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Capacidad */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Capacidad
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Capacidad (kg)"
                name="capacidad"
                type="number"
                value={formData.capacidad}
                onChange={handleInputChange}
                fullWidth
                helperText="Peso máximo de carga en kilos para esta ruta"
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Preview */}
            {formData.origen && formData.destino && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.200',
                  }}
                >
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Vista Previa de Ruta
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {getRutaVisual()}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rutas;




